import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { Typography } from '@/components/ui/typography';
import { formatShortDate } from '@/utils/date-util';

import { DateRangePanel } from '../date-range-panel';
import {
  GoalEndsLabel,
  GoalInstant,
  GoalStamp,
  GoalStartsLabel,
  GoalTimeZoneChip,
  GoalTimeZoneTooltipBody,
  getGoalWindow,
} from './goal-window';

interface HiringGoalDateRangeProps {
  startDate: string;
  endDate?: string;
  daysElapsed: number;
  addEndDateUrl: string;
  /** The opening's IANA zone; when set, start and end show their times there. */
  timeZone?: string;
  /** The instant the goal's window ends, resolved by the API. */
  endAt?: string | null;
  /** The opening's location name, named in the time zone tooltip. eg 'Dallas, TX' */
  locationName?: string;
}

// A goal with an end date but no end_at keeps the date-only panel, as the goals
// modal and the Total Hires tooltip do, rather than read as open-ended.
function useGoalWindow(
  startDate: string,
  endDate: string | undefined,
  endAt: string | null | undefined,
  timeZone: string | undefined,
) {
  return useMemo(
    () =>
      timeZone && (endAt || !endDate)
        ? getGoalWindow(startDate, endAt, timeZone)
        : null,
    [startDate, endDate, endAt, timeZone],
  );
}

export function HiringGoalDateRange({
  startDate,
  endDate,
  daysElapsed,
  addEndDateUrl,
  timeZone,
  endAt,
  locationName,
}: HiringGoalDateRangeProps) {
  const goalWindow = useGoalWindow(startDate, endDate, endAt, timeZone);

  const addEndDateLink = (
    <Link
      to={addEndDateUrl}
      target="_blank"
      className="text-fx-primary underline underline-offset-4 hover:underline"
    >
      <FormattedMessage
        defaultMessage="+ Add end date"
        description="Link to add an end date to the hiring goal"
      />
    </Link>
  );

  const elapsed = (
    <span data-testid="hiring-goal-elapsed">
      {goalWindow?.totalDays != null ? (
        <FormattedMessage
          defaultMessage="Elapsed: {count} / {total, plural, one {# day} other {# days}}"
          description="Elapsed days out of the hiring goal's length, both counted on the opening's calendar; {count} days elapsed, {total} days in the goal"
          values={{ count: daysElapsed, total: goalWindow.totalDays }}
        />
      ) : (
        <FormattedMessage
          defaultMessage="Elapsed: {count, plural, one {# day} other {# days}}"
          description="Elapsed days in the hiring goal; {count} is the number of days"
          values={{ count: daysElapsed }}
        />
      )}
    </span>
  );

  if (!timeZone || !goalWindow) {
    return (
      <DateRangePanel subtitle={elapsed}>
        <Typography
          variant="body2"
          className="text-fx-neutral-900 font-medium whitespace-nowrap"
        >
          {formatShortDate(startDate)}
          {' – '}
          {endDate ? formatShortDate(endDate) : addEndDateLink}
        </Typography>
      </DateRangePanel>
    );
  }

  return (
    <DateRangePanel
      hideCaption
      subtitle={elapsed}
      timeZoneChip={
        <GoalTimeZoneChip
          timeZone={timeZone}
          timeZoneLabel={goalWindow.timeZoneLabel}
          tooltipBody={
            <GoalTimeZoneTooltipBody
              goalWindow={goalWindow}
              locationName={locationName}
            />
          }
        />
      }
    >
      <div className="flex gap-6" data-testid="hiring-goal-window">
        <GoalStamp label={<GoalStartsLabel />}>
          <GoalInstant {...goalWindow.start} />
        </GoalStamp>
        <GoalStamp label={<GoalEndsLabel />}>
          {goalWindow.end ? (
            <GoalInstant {...goalWindow.end} />
          ) : (
            addEndDateLink
          )}
        </GoalStamp>
      </div>
    </DateRangePanel>
  );
}
